/**
 * Live capability status — single-flight, abortable, keep-last-good.
 *
 * MOUNTED ONCE, IN App.jsx. Both strips (hero and chat header) read this one
 * instance via props: the hero unmounts the moment the first message lands,
 * and two independent instances would refetch and flash a bare strip at
 * exactly that transition.
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { CAPABILITIES } from '../capabilities/registry.js';
import { deriveCapabilityState } from '../capabilities/deriveCapabilityState.js';
// This hook is the ONE fetch of /api/capabilities/status, so it is also where
// the MCP_AUTO_CONTINUE lever reaches the continuation trigger (spec §3.4) —
// a second fetch inside SolomonContext would be a second source of truth.
import { setContinuationLever } from '../context/continuation.js';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const FETCH_TIMEOUT_MS = 3000;
// A count here can only change on a deploy or a reconnect (usage is out of
// scope), so keeping the last good value through a blip is strictly better
// than blanking the strip on every alt-tab. Never fabricate — but reuse.
const STALE_AFTER_MS = 5 * 60 * 1000;

export function useCapabilityStatus(authFetch) {
  const [status, setStatus] = useState(null);
  const inFlightRef = useRef(null);
  const seqRef      = useRef(0);
  const fetchedAtRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!authFetch) return;
    // Single-flight: abort any in-flight request when a newer one starts.
    if (inFlightRef.current) inFlightRef.current.abort();
    const ctrl = new AbortController();
    inFlightRef.current = ctrl;
    const seq = ++seqRef.current;
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);

    try {
      const res = await authFetch(`${API_BASE}/api/capabilities/status`, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // A JSON-parse failure is treated exactly as a failed fetch: a proxy or
      // CDN error page can return 200 with HTML, and res.ok would pass.
      const body = await res.json();
      if (!body?.success || !body.data) throw new Error('malformed');
      if (seq !== seqRef.current) return;   // a newer request won
      setStatus(body.data);
      setContinuationLever(body.data?.continuation?.status);
      fetchedAtRef.current = Date.now();
    } catch {
      if (seq !== seqRef.current) return;
      // Keep last good until it goes stale; only then fall back to bare.
      if (fetchedAtRef.current && Date.now() - fetchedAtRef.current > STALE_AFTER_MS) {
        setStatus(null);
        setContinuationLever(null);
      }
    } finally {
      clearTimeout(timer);
      if (inFlightRef.current === ctrl) inFlightRef.current = null;
    }
  }, [authFetch]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const derived = useMemo(() => deriveCapabilityState(CAPABILITIES, status), [status]);

  return { status, derived, refresh };
}
